import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  Box, Typography, Divider, Stack, Alert, Button, Skeleton, CircularProgress,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Chip,
} from '@mui/material';
import ReceiptLongRoundedIcon from '@mui/icons-material/ReceiptLongRounded';
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import DownloadRoundedIcon from '@mui/icons-material/DownloadRounded';
import ArrowForwardRoundedIcon from '@mui/icons-material/ArrowForwardRounded';
import RefreshRoundedIcon from '@mui/icons-material/RefreshRounded';
import ArrowBackRoundedIcon from '@mui/icons-material/ArrowBackRounded';
import { useNexusBilling } from '../context/NexusBillingContext';
import { billingApi, formatPaise, FEATURE_META, describeLineUnits } from '../api/billingApi';
import {
  TOKENS, PageContainer, PageHeader, SectionCard, StatusChip, GradientButton, fadeUp,
} from '../components/NextGenPortalUI';

/**
 * NextGenOrderDetail — /nextgen-builder/orders/:orderId
 *
 * The receipt: what was bought, what was granted, what was paid, and the
 * provider references needed to trace the transaction. Nothing is computed
 * here — the order document from the backend is rendered as-is.
 */

const STATUS_TONE = {
  PAID: 'success',
  PENDING: 'pending',
  PAYMENT_INITIATED: 'processing',
  FAILED: 'failed',
  CANCELLED: 'failed',
  EXPIRED: 'failed',
  REFUNDED: 'processing',
  PARTIALLY_REFUNDED: 'processing',
};

const STATUS_LABEL = {
  PAID: 'Paid',
  PENDING: 'Pending',
  PAYMENT_INITIATED: 'Payment initiated',
  FAILED: 'Failed',
  CANCELLED: 'Cancelled',
  EXPIRED: 'Expired',
  REFUNDED: 'Refunded',
  PARTIALLY_REFUNDED: 'Partially refunded',
};

const formatDateTime = (value) => {
  if (!value) return '—';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return String(value);
  return d.toLocaleString('en-IN', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
};

const MetaRow = ({ label, value, mono }) => (
  <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1.5, py: 0.65 }}>
    <Typography variant="body2" sx={{ color: TOKENS.text.muted, fontWeight: 500, flexShrink: 0 }}>{label}</Typography>
    <Typography
      variant="body2"
      sx={{
        color: TOKENS.text.primary, fontWeight: 700, textAlign: 'right', wordBreak: 'break-all',
        ...(mono ? { fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace', fontSize: '0.78rem' } : {}),
      }}
    >
      {value || '—'}
    </Typography>
  </Box>
);

const NextGenOrderDetail = () => {
  const { orderId } = useParams();
  const navigate = useNavigate();
  const { refreshSummary } = useNexusBilling();

  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [downloading, setDownloading] = useState(false);
  const [downloadError, setDownloadError] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const data = await billingApi.getOrder(orderId);
      setOrder(data);
      if (data && data.status === 'PAID') refreshSummary();
    } catch (e) {
      if (e && e.status === 401) { navigate('/nextgen-builder/login', { state: { from: `/nextgen-builder/orders/${orderId}` } }); return; }
      setError((e && e.message) || 'We could not load this order.');
    } finally {
      setLoading(false);
    }
  }, [orderId, navigate, refreshSummary]);

  useEffect(() => { load(); }, [load]);

  const paid = order && order.status === 'PAID';
  const failed = order && ['FAILED', 'CANCELLED', 'EXPIRED'].includes(order.status);
  const pending = order && ['PENDING', 'PAYMENT_INITIATED'].includes(order.status);

  const entitlements = useMemo(() => (order && Array.isArray(order.entitlements) ? order.entitlements : []), [order]);

  const handleDownload = async () => {
    setDownloading(true);
    setDownloadError('');
    try {
      const data = await billingApi.downloadInvoice(order.id);
      const blob = data instanceof Blob ? data : new Blob([data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${order.invoiceNumber || order.orderNumber || `order-${order.id}`}.pdf`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      setDownloadError((e && e.message) || 'Failed to download the invoice.');
    } finally {
      setDownloading(false);
    }
  };

  if (loading) {
    return (
      <PageContainer maxWidth={1040}>
        <Stack spacing={2.5}>
          <Skeleton variant="text" width={260} height={44} />
          <Skeleton variant="rectangular" height={140} sx={{ borderRadius: TOKENS.radius.card }} />
          <Skeleton variant="rectangular" height={260} sx={{ borderRadius: TOKENS.radius.card }} />
        </Stack>
      </PageContainer>
    );
  }

  if (error || !order) {
    return (
      <PageContainer maxWidth={1040}>
        <Alert
          severity="error"
          sx={{ borderRadius: TOKENS.radius.control }}
          action={<Button size="small" onClick={load} startIcon={<RefreshRoundedIcon sx={{ fontSize: '16px !important' }} />} sx={{ fontWeight: 700, textTransform: 'none' }}>Retry</Button>}
        >
          {error || 'Order not found.'}
        </Alert>
        <Button
          startIcon={<ArrowBackRoundedIcon />}
          onClick={() => navigate('/nextgen-builder/payments')}
          sx={{ mt: 2, fontWeight: 700, textTransform: 'none', color: TOKENS.text.caption }}
        >
          Back to payments
        </Button>
      </PageContainer>
    );
  }

  const taxLabel = `${order.taxCode || 'Tax'} (${order.taxPercent}%)`;

  return (
    <PageContainer maxWidth={1040}>
      <PageHeader
        icon={<ReceiptLongRoundedIcon />}
        eyebrow="TSG Nexus"
        title={`Order ${order.orderNumber}`}
        description={`Placed ${formatDateTime(order.createdAt)}`}
        action={
          <Stack direction="row" spacing={1}>
            <Button
              startIcon={<ArrowBackRoundedIcon />}
              onClick={() => navigate('/nextgen-builder/payments')}
              sx={{
                borderRadius: TOKENS.radius.control, fontWeight: 700, textTransform: 'none',
                color: TOKENS.text.caption, border: `1px solid ${TOKENS.border}`, px: 2,
                '&:hover': { bgcolor: TOKENS.bg.page },
              }}
            >
              All orders
            </Button>
            {order.invoiceNumber && (
              <Button
                startIcon={downloading ? <CircularProgress size={14} /> : <DownloadRoundedIcon />}
                onClick={handleDownload}
                disabled={downloading}
                sx={{
                  borderRadius: TOKENS.radius.control, fontWeight: 700, textTransform: 'none',
                  color: TOKENS.brand.primary, border: '1px solid rgba(108,59,255,0.35)', px: 2,
                  '&:hover': { bgcolor: 'rgba(108,59,255,0.06)' },
                }}
              >
                Download invoice (PDF)
              </Button>
            )}
          </Stack>
        }
      />

      {downloadError && (
        <Alert severity="error" sx={{ mb: 3, borderRadius: TOKENS.radius.control }} onClose={() => setDownloadError('')}>
          {downloadError}
        </Alert>
      )}

      {/* ── Status hero ── */}
      {paid ? (
        <SectionCard
          component={motion.div}
          {...fadeUp}
          sx={{
            mb: 3,
            background: 'linear-gradient(135deg, rgba(22,163,74,0.07), rgba(22,163,74,0.02))',
            border: '1px solid rgba(22,163,74,0.25)',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.75 }}>
            <Box
              sx={{
                width: 44, height: 44, borderRadius: '12px', flexShrink: 0,
                bgcolor: 'rgba(22,163,74,0.12)', color: '#16A34A',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}
            >
              <CheckCircleRoundedIcon sx={{ fontSize: 24 }} />
            </Box>
            <Box sx={{ minWidth: 0, flex: 1 }}>
              <Typography sx={{ fontWeight: 800, color: TOKENS.text.primary, fontSize: '1.06rem' }}>
                Payment successful — your credits are ready
              </Typography>
              <Typography variant="body2" sx={{ color: TOKENS.text.muted, fontWeight: 500, mt: 0.35 }}>
                Paid {formatDateTime(order.paidAt)}. Purchased credits do not expire at the end of the month.
              </Typography>

              {entitlements.length > 0 && (
                <Stack spacing={1} sx={{ mt: 2 }}>
                  {entitlements.map((ent) => {
                    const meta = FEATURE_META[ent.feature] || {};
                    const remaining = (ent.unitsTotal || 0) - (ent.unitsUsed || 0);
                    const route = meta.route
                      ? (ent.technology ? `${meta.route}/${ent.technology}` : meta.route)
                      : null;
                    return (
                      <Box
                        key={ent.id}
                        sx={{
                          display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1.5, flexWrap: 'wrap',
                          p: 1.4, borderRadius: TOKENS.radius.control,
                          background: TOKENS.bg.card, border: `1px solid ${TOKENS.borderSoft}`,
                        }}
                      >
                        <Box sx={{ minWidth: 0 }}>
                          <Typography variant="body2" sx={{ fontWeight: 800, color: TOKENS.text.primary }}>
                            {meta.label || ent.feature}
                            {ent.technology ? ` · ${ent.technology}` : ''}
                          </Typography>
                          <Typography variant="caption" sx={{ color: TOKENS.text.muted, fontWeight: 600 }}>
                            {ent.accessType === 'FULL_ACCESS'
                              ? (ent.expiresAt
                                ? `${ent.status === 'ACTIVE' && new Date(ent.expiresAt) > new Date() ? 'Full access until' : 'Full access ended'} ${new Date(ent.expiresAt).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}`
                                : 'Full access')
                              : `${remaining} of ${ent.unitsTotal} ${meta.units || 'units'} available`}
                          </Typography>
                        </Box>
                        {route && (
                          <Button
                            size="small"
                            endIcon={<ArrowForwardRoundedIcon sx={{ fontSize: '15px !important' }} />}
                            onClick={() => navigate(route)}
                            sx={{ fontWeight: 800, textTransform: 'none', color: TOKENS.brand.primary }}
                          >
                            Open {meta.label || 'feature'}
                          </Button>
                        )}
                      </Box>
                    );
                  })}
                </Stack>
              )}
            </Box>
            <StatusChip label={STATUS_LABEL[order.status] || order.status} tone={STATUS_TONE[order.status] || 'default'} />
          </Box>
        </SectionCard>
      ) : (
        <SectionCard
          component={motion.div}
          {...fadeUp}
          sx={{
            mb: 3,
            background: failed ? 'rgba(239,68,68,0.04)' : 'rgba(245,158,11,0.05)',
            border: `1px solid ${failed ? 'rgba(239,68,68,0.24)' : 'rgba(245,158,11,0.28)'}`,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 1.5, flexWrap: 'wrap' }}>
            <Box sx={{ minWidth: 0 }}>
              <Typography sx={{ fontWeight: 800, color: TOKENS.text.primary, fontSize: '1.02rem' }}>
                {failed ? 'This order was not paid' : 'Payment is still pending'}
              </Typography>
              <Typography variant="body2" sx={{ color: TOKENS.text.muted, fontWeight: 500, mt: 0.35, maxWidth: 620 }}>
                {failed
                  ? (order.failureReason || 'No credits were added and you have not been charged. Your cart is preserved, so you can try the payment again.')
                  : 'We have not received a confirmed payment for this order yet. If money was deducted, it will be reconciled automatically within a few minutes.'}
              </Typography>
              <Stack direction="row" spacing={1} sx={{ mt: 2, flexWrap: 'wrap', gap: 1 }}>
                <GradientButton onClick={() => navigate('/nextgen-builder/checkout')} sx={{ textTransform: 'none' }}>
                  {failed ? 'Try payment again' : 'Return to checkout'}
                </GradientButton>
                <Button
                  startIcon={<RefreshRoundedIcon />}
                  onClick={load}
                  sx={{ borderRadius: TOKENS.radius.control, fontWeight: 700, textTransform: 'none', color: TOKENS.text.caption, border: `1px solid ${TOKENS.border}` }}
                >
                  Refresh status
                </Button>
              </Stack>
            </Box>
            <StatusChip label={STATUS_LABEL[order.status] || order.status} tone={STATUS_TONE[order.status] || 'default'} />
          </Box>
        </SectionCard>
      )}

      <Box sx={{ display: 'grid', gap: 3, gridTemplateColumns: { xs: '1fr', md: 'minmax(0, 1fr) 320px' }, alignItems: 'start' }}>
        {/* ── Items ── */}
        <SectionCard component={motion.div} {...fadeUp} padding={0} sx={{ p: 0, overflow: 'hidden' }}>
          <Box sx={{ px: { xs: 2.25, md: 3 }, pt: 2.5, pb: 1.75 }}>
            <Typography sx={{ fontWeight: 800, color: TOKENS.text.primary, fontSize: '0.98rem' }}>Items</Typography>
          </Box>
          <Divider />
          <TableContainer sx={{ display: { xs: 'none', sm: 'block' } }}>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ '& th': { fontWeight: 800, color: TOKENS.text.muted, fontSize: '0.72rem', textTransform: 'uppercase', letterSpacing: '0.06em' } }}>
                  <TableCell>Product</TableCell>
                  <TableCell>Details</TableCell>
                  <TableCell align="right">Unit price</TableCell>
                  <TableCell align="center">Qty</TableCell>
                  <TableCell align="right">Total</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {(order.items || []).map((item) => (
                  <TableRow key={item.id} sx={{ '& td': { borderBottom: `1px solid ${TOKENS.borderSoft}`, py: 1.5 } }}>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontWeight: 800, color: TOKENS.text.primary, fontSize: '0.86rem' }}>
                        {item.productName}
                      </Typography>
                      <Typography variant="caption" sx={{ color: TOKENS.text.muted, fontWeight: 600 }}>
                        {(FEATURE_META[item.feature] || {}).label || item.feature}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="caption" sx={{ color: TOKENS.text.muted, fontWeight: 600 }}>
                        {describeLineUnits(item)}{item.technologyTitle ? ` · ${item.technologyTitle}` : ''}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Typography variant="body2" sx={{ fontWeight: 700, color: TOKENS.text.secondary, fontSize: '0.84rem' }}>
                        {formatPaise(item.unitPricePaise)}
                      </Typography>
                    </TableCell>
                    <TableCell align="center">
                      <Typography variant="body2" sx={{ fontWeight: 700, color: TOKENS.text.secondary, fontSize: '0.84rem' }}>
                        {item.quantity}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Typography variant="body2" sx={{ fontWeight: 800, color: TOKENS.text.primary, fontSize: '0.88rem' }}>
                        {formatPaise(item.subtotalPaise)}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          <Stack sx={{ display: { xs: 'flex', sm: 'none' } }} divider={<Divider />}>
            {(order.items || []).map((item) => (
              <Box key={item.id} sx={{ p: 2.25 }}>
                <Typography variant="body2" sx={{ fontWeight: 800, color: TOKENS.text.primary }}>{item.productName}</Typography>
                <Typography variant="caption" sx={{ color: TOKENS.text.muted, fontWeight: 600, display: 'block', mt: 0.25 }}>
                  {describeLineUnits(item)}{item.technologyTitle ? ` · ${item.technologyTitle}` : ''}
                </Typography>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 1 }}>
                  <Typography variant="caption" sx={{ color: TOKENS.text.muted, fontWeight: 600 }}>
                    {formatPaise(item.unitPricePaise)} × {item.quantity}
                  </Typography>
                  <Typography variant="body2" sx={{ fontWeight: 800, color: TOKENS.text.primary }}>
                    {formatPaise(item.subtotalPaise)}
                  </Typography>
                </Box>
              </Box>
            ))}
          </Stack>

          <Divider />
          <Box sx={{ px: { xs: 2.25, md: 3 }, py: 2.25 }}>
            <Stack spacing={1} sx={{ maxWidth: 360, ml: 'auto' }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Typography variant="body2" sx={{ color: TOKENS.text.muted, fontWeight: 500 }}>Subtotal</Typography>
                <Typography variant="body2" sx={{ color: TOKENS.text.primary, fontWeight: 700 }}>{formatPaise(order.subtotalPaise)}</Typography>
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Typography variant="body2" sx={{ color: TOKENS.text.muted, fontWeight: 500 }}>{taxLabel}</Typography>
                <Typography variant="body2" sx={{ color: TOKENS.text.primary, fontWeight: 700 }}>{formatPaise(order.taxPaise)}</Typography>
              </Box>
              {order.gstType === 'CGST_SGST' ? (
                <Typography variant="caption" sx={{ color: TOKENS.text.muted, fontWeight: 500 }}>
                  CGST {formatPaise(order.cgstPaise)} · SGST {formatPaise(order.sgstPaise)}
                  {order.customerState ? ` · ${order.customerState}` : ''}
                </Typography>
              ) : order.gstType === 'IGST' ? (
                <Typography variant="caption" sx={{ color: TOKENS.text.muted, fontWeight: 500 }}>
                  IGST {formatPaise(order.igstPaise)}{order.customerState ? ` · ${order.customerState}` : ''}
                </Typography>
              ) : null}
              <Divider sx={{ borderStyle: 'dashed', my: 0.5 }} />
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
                <Typography sx={{ fontWeight: 800, color: TOKENS.text.primary }}>Grand total</Typography>
                <Typography sx={{ fontWeight: 900, color: TOKENS.text.primary, fontSize: '1.2rem', letterSpacing: '-0.02em' }}>
                  {formatPaise(order.grandTotalPaise)}
                </Typography>
              </Box>
            </Stack>
          </Box>
        </SectionCard>

        {/* ── References ── */}
        <SectionCard component={motion.div} {...fadeUp}>
          <Typography sx={{ fontWeight: 800, color: TOKENS.text.primary, fontSize: '0.98rem', mb: 1.25 }}>
            Order details
          </Typography>
          <Stack divider={<Divider sx={{ borderColor: TOKENS.borderSoft }} />}>
            <MetaRow label="Order number" value={order.orderNumber} mono />
            <MetaRow label="Status" value={STATUS_LABEL[order.status] || order.status} />
            <MetaRow label="Placed" value={formatDateTime(order.createdAt)} />
            {order.paidAt && <MetaRow label="Paid" value={formatDateTime(order.paidAt)} />}
            <MetaRow label="Razorpay order" value={order.providerOrderId} mono />
            <MetaRow label="Razorpay payment" value={order.providerPaymentId} mono />
            <MetaRow label="Invoice number" value={order.invoiceNumber} mono />
            <MetaRow label="Invoice date" value={order.invoiceDate ? formatDateTime(order.invoiceDate) : '—'} />
          </Stack>

          {Array.isArray(order.payments) && order.payments.length > 0 && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="caption" sx={{ fontWeight: 800, color: TOKENS.text.muted, textTransform: 'uppercase', letterSpacing: '0.06em', fontSize: '0.66rem' }}>
                Payment attempts
              </Typography>
              <Stack spacing={1} sx={{ mt: 1 }}>
                {order.payments.map((p) => (
                  <Box key={p.id} sx={{ p: 1.25, borderRadius: TOKENS.radius.control, background: TOKENS.bg.page, border: `1px solid ${TOKENS.borderSoft}` }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 1 }}>
                      <Typography variant="caption" sx={{ fontWeight: 700, color: TOKENS.text.caption, wordBreak: 'break-all' }}>
                        {p.providerPaymentId || '—'}
                      </Typography>
                      <Chip
                        size="small"
                        label={p.status}
                        sx={{
                          height: 20, fontWeight: 700, fontSize: '0.64rem',
                          bgcolor: p.status === 'CAPTURED' ? 'rgba(22,163,74,0.1)' : 'rgba(239,68,68,0.1)',
                          color: p.status === 'CAPTURED' ? '#16A34A' : '#DC2626',
                        }}
                      />
                    </Box>
                    <Typography variant="caption" sx={{ color: TOKENS.text.muted, display: 'block', mt: 0.25 }}>
                      {formatPaise(p.amountPaise)}{p.method ? ` · ${p.method}` : ''}{p.verifiedVia ? ` · ${p.verifiedVia === 'WEBHOOK' ? 'verified by webhook' : 'verified at checkout'}` : ''}
                    </Typography>
                    {p.errorDescription && (
                      <Typography variant="caption" sx={{ color: '#DC2626', display: 'block', fontWeight: 600 }}>
                        {p.errorDescription}
                      </Typography>
                    )}
                  </Box>
                ))}
              </Stack>
            </Box>
          )}

          {order.invoiceNumber && (
            <Button
              fullWidth
              startIcon={downloading ? <CircularProgress size={14} /> : <DownloadRoundedIcon />}
              onClick={handleDownload}
              disabled={downloading}
              sx={{
                mt: 2, borderRadius: TOKENS.radius.control, fontWeight: 700, textTransform: 'none',
                color: TOKENS.brand.primary, border: '1px solid rgba(108,59,255,0.35)',
                '&:hover': { bgcolor: 'rgba(108,59,255,0.06)' },
              }}
            >
              Download invoice (PDF)
            </Button>
          )}
        </SectionCard>
      </Box>
    </PageContainer>
  );
};

export default NextGenOrderDetail;
